/**
 * liveSessionBadgesFormatter — ce qu'une séance MONTRE de son état.
 *
 * Une séance peut porter cinq statuts à la fois (du jour, replanifiée, terminée, en retard, en
 * rattrapage). Les afficher tous, c'est n'en lire aucun : la règle en garde **un seul d'état, et au
 * plus un de contexte**. C'est une règle d'AFFICHAGE, lue par toutes les surfaces qui montrent une
 * séance (`design-system/hooks/useSessionBadges`) ; `statuses` reste entier, c'est lui que lisent
 * les gestes (`liveSessionActions`).
 */
import { isToday } from "./dateFormatter"
import { isSessionInProgress, sessionCountdown } from "./liveSessionFormatter"
import {
    type UILiveSession,
    type UILiveSessionBadges,
    type UILiveSessionContextBadge,
    type UILiveSessionStateBadge,
    type UILiveSessionStatus,
} from "../types"

/**
 * Les états qui FERMENT la séance, dans l'ordre où ils l'emportent : terminée, suspendue pour
 * paiement, absence signalée. Chacun s'affiche seul — plus rien d'autre n'est à dire d'une séance
 * où l'enfant n'entrera pas.
 */
const CLOSING_STATES: Extract<UILiveSessionStatus, UILiveSessionStateBadge>[] = [
    "finished",
    "payment_blocked",
    "absent",
]

/**
 * Les badges de contexte, du plus au moins prioritaire : un retard signalé, un remplaçant, un
 * rattrapage (ses deux formes ont le même rang), une replanification. Un seul s'affiche.
 */
export const SESSION_CONTEXT_BADGES: UILiveSessionContextBadge[] = [
    "late",
    "substitute",
    "makeup_recovery",
    "makeup_temporary",
    "rescheduled",
]

/**
 * Les badges à montrer pour cette séance, à cet instant — `null` quand elle n'en a aucun.
 *
 * L'état, le premier qui s'applique :
 *  1. terminée → « Terminé », seul ;
 *  2. paiement en attente → seul ;
 *  3. absence signalée → seule ;
 *  4. en cours (de son début à sa fin) → « En cours », sans décompte ;
 *  5. aujourd'hui → « Aujourd'hui », et le décompte tant qu'elle n'a pas commencé ;
 *  6. un autre jour → aucun.
 * Le contexte ne s'ajoute qu'aux états 4, 5 et 6.
 *
 * `today: false` là où le jour est déjà écrit (une ligne, sous sa date) : ni « Aujourd'hui », ni
 * son décompte. « En cours » reste — l'heure de la ligne ne le dit pas.
 *
 * `now` (en millisecondes) est l'horloge de l'appelant.
 */
export function sessionBadges(
    session: Pick<UILiveSession, "date" | "duration" | "statuses">,
    now: number = Date.now(),
    options: { today?: boolean } = {}
): UILiveSessionBadges | null {
    const { statuses } = session

    const closing = CLOSING_STATES.find((state) => statuses.includes(state))
    if (closing != null) return { state: closing, countdown: null, context: null }

    const context = SESSION_CONTEXT_BADGES.find((badge) => statuses.includes(badge)) ?? null

    if (isSessionInProgress(session, now)) return { state: "in_progress", countdown: null, context }
    if ((options.today ?? true) && isToday(session.date, now)) {
        return { state: "today", countdown: sessionCountdown(session, now), context }
    }
    return context != null ? { state: null, countdown: null, context } : null
}
